import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2 } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, EmptyState, ErrorState } from '@/acc-ui/primitives/Layout';
import { Button } from '@/acc-ui/primitives/Button';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { notificationsApi, type NotificationEntryDTO } from '@/api/notifications-api';
import { cn } from '@/lib/utils';

/**
 * Notifications center — full paginated list at /notifications.
 *
 * Supports filter for unread-only, per-item mark-read / delete, and
 * mark-all-read.
 */

export function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [toDelete, setToDelete] = useState<NotificationEntryDTO | null>(null);

  const query = useQuery({
    queryKey: ['notificationsPage', { page, pageSize, unreadOnly }],
    queryFn: () => notificationsApi.list({ page, pageSize, unreadOnly }),
    placeholderData: (prev) => prev,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[]) => notificationsApi.markRead(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notificationsPage'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notificationsPage'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => notificationsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notificationsPage'] });
    },
  });

  const handleOpen = (n: NotificationEntryDTO) => {
    if (!n.isRead) markRead.mutate([n.id]);
    if (n.linkPath) navigate(n.linkPath);
  };

  const data = query.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <AppShell crumb="Workspace" title="Notifications">
      <PageHeader
        title="Notifications"
        subtitle="All alerts sent to you, across every module."
        actions={
          data && data.unread > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Check className="w-4 h-4" />}
              onClick={() => markAllRead.mutate()}
              loading={markAllRead.isPending}
            >
              Mark all as read
            </Button>
          ) : null
        }
      />

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={unreadOnly ? 'primary' : 'secondary'}
          onClick={() => {
            setUnreadOnly(false);
            setPage(1);
          }}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={unreadOnly ? 'secondary' : 'primary'}
          onClick={() => {
            setUnreadOnly(true);
            setPage(1);
          }}
        >
          Unread
          {data && data.unread > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-brand-blue text-white text-micro">
              {data.unread}
            </span>
          )}
        </Button>
      </div>

      {query.isError ? (
        <ErrorState
          description="Could not load notifications."
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : data && data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Bell className="w-5 h-5" />}
            title={unreadOnly ? 'No unread notifications' : 'No notifications yet'}
            description="As other modules (validation, ingestion, detection) produce events relevant to you, they will appear here."
          />
        </Card>
      ) : (
        <Card padding={false}>
          <ul className="divide-y divide-glass-hairline">
            {(data?.items ?? []).map((n) => (
              <li key={n.id} className={cn('flex items-start gap-3 px-4 py-3', !n.isRead && 'bg-brand-blue/[0.03]')}>
                <span
                  className={cn(
                    'mt-2 w-2 h-2 rounded-full shrink-0',
                    n.isRead
                      ? 'bg-slate-300'
                      : n.severity === 'critical'
                      ? 'bg-red-500'
                      : n.severity === 'warning'
                      ? 'bg-amber-500'
                      : 'bg-brand-blue',
                  )}
                  aria-hidden
                />
                <button
                  onClick={() => handleOpen(n)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('text-label', !n.isRead ? 'font-semibold text-text-primary' : 'text-text-secondary')}>
                      {n.title}
                    </span>
                    <Badge tone={n.severity === 'critical' ? 'danger' : n.severity === 'warning' ? 'warning' : 'info'}>
                      {n.severity}
                    </Badge>
                    <span className="text-micro text-text-tertiary tabular-nums">
                      {new Date(n.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {n.body && (
                    <p className="mt-0.5 text-caption text-text-tertiary leading-relaxed line-clamp-2">
                      {n.body}
                    </p>
                  )}
                  <div className="mt-0.5 text-micro text-text-muted">
                    {n.source}
                    {n.linkPath && (
                      <span> · <code className="font-mono">{n.linkPath}</code></span>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {!n.isRead && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Check className="w-3.5 h-3.5" />}
                      onClick={() => markRead.mutate([n.id])}
                      aria-label="Mark as read"
                    >
                      Read
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                    onClick={() => setToDelete(n)}
                    aria-label="Delete"
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          {/* Pagination footer */}
          {data && data.total > pageSize && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-glass-hairline">
              <span className="text-caption text-text-tertiary">
                Page {page} of {totalPages} · {data.total} total
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog
        open={toDelete !== null}
        onOpenChange={(v) => !v && setToDelete(null)}
        title="Delete notification?"
        description={toDelete ? `"${toDelete.title}" will be permanently removed.` : undefined}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id, { onSuccess: () => setToDelete(null) });
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-caption text-text-tertiary">This cannot be undone.</p>
      </Dialog>
    </AppShell>
  );
}
